// Semantic chip/status tints for mgPris: three background levels plus a text colour that is
// readable on all three, in both colour schemes. Light values are the pastels used before.
export interface SemanticTint {
  soft: string;
  medium: string;
  strong: string;
  text: string;
}

export type SemanticTintName =
  | 'green'
  | 'saffron'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'pink'
  | 'sky'
  | 'lavender';

export interface SemanticPalette {
  tint: Record<SemanticTintName, SemanticTint>;
  status: {
    positive: SemanticTint;
    pending: SemanticTint;
    negative: SemanticTint;
  };
  action: {
    create: SemanticTint;
    read: SemanticTint;
    update: SemanticTint;
    delete: SemanticTint;
  };
}

const withAliases = (tint: SemanticPalette['tint']): SemanticPalette => ({
  tint,
  status: { positive: tint.green, pending: tint.saffron, negative: tint.red },
  action: { create: tint.green, read: tint.sky, update: tint.yellow, delete: tint.red },
});

const lightText = '#3E3F5B';

export const semanticLight: SemanticPalette = withAliases({
  green: { soft: '#20A32D33', medium: '#20A32D4D', strong: '#20A32D80', text: lightText },
  saffron: { soft: '#FFEFCF', medium: '#FFE6B7', strong: '#FFD687', text: lightText },
  yellow: { soft: '#FCF3C8', medium: '#F8E691', strong: '#F2DC5C', text: lightText },
  orange: { soft: '#FFE5BF', medium: '#FFCC80', strong: '#FF9800', text: lightText },
  red: { soft: '#FBEBE9', medium: '#FFB9B0', strong: '#FF8E7E', text: lightText },
  pink: { soft: '#FFE8EE', medium: '#FFD1DC', strong: '#FFB3C6', text: lightText },
  sky: { soft: '#B1E5FC66', medium: '#B1E5FC80', strong: '#B1E5FC', text: lightText },
  lavender: { soft: '#C0B6F44D', medium: '#C0B6F4', strong: '#B3A8F3', text: lightText },
});

export const semanticDark: SemanticPalette = withAliases({
  green: { soft: '#1E3936', medium: '#1F4735', strong: '#1F5A33', text: '#9BD6A0' },
  saffron: { soft: '#4B3B30', medium: '#624A2C', strong: '#835F26', text: '#FFE2AB' },
  yellow: { soft: '#464135', medium: '#5B5234', strong: '#796C32', text: '#F9F2CA' },
  orange: { soft: '#4B372D', medium: '#624327', strong: '#83551F', text: '#FFD699' },
  red: { soft: '#45293C', medium: '#582E3D', strong: '#753640', text: '#F0A9A5' },
  pink: { soft: '#482C4A', medium: '#5D3353', strong: '#7D3D61', text: '#F8B8CE' },
  sky: { soft: '#28405E', medium: '#2D5071', strong: '#34698E', text: '#C1EAFC' },
  lavender: { soft: '#34325D', medium: '#403B6F', strong: '#50498B', text: '#CCC4F8' },
});
